/**
 * @module Messages.Styles
 * @description Styled components for the Messages page in the mental health portal.
 * Provides styling for the message categories, message list, and message content sections.
 */

import styled from '@emotion/styled/macro';
import { Autocomplete } from '@mui/material';

/**
 * @component PageTitle
 * @description Main page title styling
 */
export const PageTitle = styled.h1`
  color: #444;
  font-size: 2rem;
  font-weight: 400;
`;

/**
 * @component PageTitleButton
 * @description Back button styling in the page title
 */
export const PageTitleButton = styled.button`
  all: unset; /* Remove todo o estilo padrão do botão */
  color: #444;
  font-size: 2rem;
  font-weight: 400;
  cursor: pointer; /* Se quiser indicar que é clicável */
`;

/**
 * @component Main
 * @description Main container for the Messages page with responsive layout based on props
 * @param {boolean} props.isLoading - Whether content is loading
 * @param {boolean} props.hasCategories - Whether categories exist
 * @param {boolean} props.hasFilteredMessages - Whether filtered messages exist
 */
export const Main = styled.main`
  height: 100%;
  max-height: 85vh;
  max-width: 100%;
  margin-top: 2rem;
  background-color: #ffffff;
  box-shadow: -2px 1px 7px -6px rgba(0, 0, 0, 0.6);
  box-sizing: border-box;

  ${(props) => {
    if (props.isLoading) {
      return `
        display: flex;
        justify-content: center;
        align-items: center;
      `;
    }

    if (!props.hasCategories) {
      return `
         display: flex;
         flex-direction: column;

        p {
          text-align: center;
          grid-column: 1 /-1;
          padding: 8rem 0;
          align-self: center;
          color: #888;
          font-size: 1.2rem;
        }
      `;
    }

    if (!props.hasFilteredMessages) {
      return `
        display: grid;
        grid-template-columns: 20% 30% 50%;
        grid-template-rows: 7.9rem auto;

        & > p {
          grid-column: 2 / 4;
          grid-row: 2;
          text-align: center;
          align-self: center;
          justify-self: center;
          color: #888;
          font-size: 1.2rem;
        }
      `;
    }

    return `
      display: grid;
      grid-template-columns: 20% 30% 50%;
      grid-template-rows: 7.9rem auto;
    `;
  }}
`;

/**
 * @component HeaderContainer
 * @description Container for the header section with grid layout
 */
export const HeaderContainer = styled.header`
  display: grid;
  grid-template-columns: 20% 30% 50%;
  grid-column: 1 / -1;
  grid-row: 1;

  max-width: 100%;
`;

/**
 * @component HeaderSection
 * @description Section within the header with flexible alignment
 * @param {string} props.align - Alignment of content ('center', 'right', 'space-between')
 * @param {string} props.flexDirection - Direction of flex items ('column' or 'row')
 */
export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  padding: 0 1.5rem;

  gap: 1rem;

  justify-content: ${(props) => {
    if (props.align === 'center') return 'center';
    if (props.align === 'right') return 'flex-end';
    if (props.align === 'space-between') return 'space-between';
    return 'flex-start'; // default
  }};

  flex-direction: ${(props) => {
    if (props.flexDirection === 'column') return 'column';
    return 'row'; // default
  }};

  height: 100%;
  min-height: 8rem;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);

  .buttons-card {
    display: flex;
    align-items: center;
    gap: 10px;
    transition:
      transform 0.3s,
      box-shadow 0.3s;

    button {
      border: none;
      background-color: transparent;
      cursor: pointer;
    }

    button:hover {
      transform: translateY(-1.5px);
    }
  }
`;

/**
 * @component ButtonStyled
 * @description Styled button with hover and active states
 */
export const ButtonStyled = styled.button`
  min-width: 8rem;
  max-width: ${({ maxWidth }) => maxWidth || '100%'};
  padding: 0.8rem 0.3rem;

  font-size: 0.9rem;
  border: none;
  border-radius: 0.3125rem;
  border-inline: 0.5px solid transparent;

  background-color: ${({ bgcolor }) => bgcolor || '#3b86ff'};
  color: #ffffff;

  box-shadow: -2px 1px 7px -5px rgba(0, 0, 0, 0.6);
  transition: all 0.1s ease-in-out;

  :hover {
    cursor: pointer;
  }

  :active {
    transform: scale(0.98);
    background-color: ${({ bgcoloractive }) => bgcoloractive || '#3275e0'};
    box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.2);
  }

  :focus {
    outline: none;
  }
`;

/**
 * @component HeaderTitle
 * @description Title styling for headers
 */
export const HeaderTitle = styled.h2`
  color: #444;
  font-size: 1.3rem;
  font-weight: 400;
`;

/**
 * @component SectionCategory
 * @description Container for the category list section with custom scrollbar
 */
export const SectionCategory = styled.section`
  display: grid;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  width: 100%;
  overflow-x: hidden;
  overflow-y: scroll;

  /* Personalizando a barra de rolagem diretamente no componente */
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(207, 207, 207, 0.64);
    border-radius: 4px;
  }
`;

/**
 * @component CategoryList
 * @description List container for categories
 */
export const CategoryList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1.2rem;
`;

/**
 * @component CategoryItem
 * @description Individual category item with active state styling
 * @param {boolean} props.isActive - Whether the category is currently selected
 */
export const CategoryItem = styled.li`
  display: flex;
  justify-content: space-between;
  list-style: none;
  cursor: pointer;
  color: #666666;
  padding: 0.5rem 0.2rem;
  transition: all 0.3s ease;
  font-size: 0.95rem;

  /* Efeito ao passar o mouse (hover) */
  &:hover {
    color: #3b86ff;
    background-color: #f0f8ff; /* Adicionando um fundo leve ao passar o mouse */
  }

  /* Efeito quando o item estiver ativo (clicado ou selecionado) */
  ${({ isActive }) =>
    isActive &&
    `
      color: #3b86ff;
      font-weight: 600; /* Negrito mais suave */
    `}

  /* Remove o contorno padrão quando o item é focado */
  &:focus-visible {
    outline: none;
  }

  /* Efeito ao clicar (ativo) */
  &:active {
    color: #3b86ff;
    font-weight: 600; /* Negrito mais suave ao clicar */
    background-color: #e0f0ff; /* Cor de fundo para manter o destaque */
  }
`;

/**
 * @component SectionMessage
 * @description Container for the message list section
 */
export const SectionMessage = styled.section`
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
`;

/**
 * @component MessageList
 * @description List container for messages with custom scrollbar
 */
export const MessageList = styled.ul`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(207, 207, 207, 0.64);
    border-radius: 4px;
  }
`;

/**
 * @component MessageItem
 * @description Individual message item with active state styling
 * @param {boolean} props.isActive - Whether the message is currently selected
 */
export const MessageItem = styled.li`
  display: flex;
  justify-content: space-between;
  width: 100%;
  background-color: #ffffff;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  cursor: pointer;
  box-sizing: border-box;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(207, 207, 207, 0.64);

  &:nth-of-type(2n + 1) {
    border-left: 3px solid #a3a0fb;
  }

  &:nth-of-type(2n + 2) {
    border-left: 3px solid #3b86ff;
  }

  ${({ isActive }) =>
    isActive &&
    `
    background:rgb(241, 241, 241);
    border-left: 3px solid #ff6565 !important;
    `}

  .info-card {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  span {
    font-weight: 500;
  }

  p {
    font-size: 0.72rem;
  }

  .buttons-card {
    display: flex;
    align-items: center;
    gap: 10px;
    transition:
      transform 0.3s,
      box-shadow 0.3s;

    button {
      border: none;
      background-color: transparent;
      cursor: pointer;
    }

    button:hover {
      transform: translateY(-1.5px);
    }
  }
`;

/**
 * @component SectionContentMessage
 * @description Container for displaying the selected message content with custom scrollbar
 */
export const SectionContentMessage = styled.section`
  display: grid;
  max-width: 100%;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  overflow-x: hidden;
  overflow-y: hidden;

  padding: 2.5rem;
  max-height: 100%;
  overflow-y: auto;

  p {
    font-size: 14px;
    text-align: left;
  }

  ::-webkit-scrollbar {
    width: 5px;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(207, 207, 207, 0.64);
    border-radius: 4px;
  }
`;

/**
 * @component Container
 * @description General container for page content
 */
export const Container = styled.main`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 2em 5em;
  background-color: #f0f0f7;
`;

/**
 * @component Content
 * @description Content container with grid layout
 */
export const Content = styled.div`
  display: grid;
  grid-template-rows: 1fr 8fr;
  padding: 20px;
  height: 100%;
  margin-top: 1em;
  background-color: #fff;
`;

/**
 * @component ContentItem
 * @description Individual content item with centered content
 */
export const ContentItem = styled.div`
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #fafafa;
  box-sizing: border-box;
`;

/**
 * @component MessageFormSection
 * @description Form section for creating/editing messages
 */
export const MessageFormSection = styled.section`
  display: grid;
  grid-column: 2 / -1;
  grid-row: 1 / -1;

  padding: 2rem 4rem;

  background-color: #fff;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  border-left: 1px solid rgba(207, 207, 207, 0.64);

  max-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;

  form {
    display: flex;
    gap: 1rem;
    flex-direction: column;
  }

  select,
  input,
  textarea {
    color: rgb(67, 67, 67);
    border: 1px solid rgba(207, 207, 207, 0.64);
    border-radius: 0.3rem;
    background-color: #fff;
    transition:
      border-color 0.3s,
      color 0.3s;
  }

  select {
    height: 3rem;
    padding: 0 1.7rem;
  }

  input {
    height: 3rem;
    padding: 0 1.7rem;
  }

  textarea {
    height: 60%;
    padding: 1rem 1.7rem;
    resize: none;
  }

  select:focus,
  input:focus,
  textarea:focus {
    border-color: #000;
    color: #000;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
    outline: none;
  }

  .buttons-area {
    display: flex;
    justify-content: end;
    column-gap: 1rem;
    width: 100%;
  }

  .error {
    border: 1px solid red !important;
    outline: red;
  }
`;

/**
 * @component CategoryFormSection
 * @description Form section for creating/editing categories
 */
export const CategoryFormSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  form {
    display: flex;
    flex-direction: column;
    padding-top: 1rem;
    gap: 1rem;

    input {
      height: 2.5rem;
      width: 95%;
      border: 0.5px solid rgba(207, 207, 207, 0.69);
      border-radius: 2px;
      font-size: 0.8rem;
      color: rgb(68, 68, 68);
      padding-left: 15px;
      align-items: center;
      box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
      outline: none;
    }

    div {
      display: flex;
      justify-content: end;
    }

    .error {
      border: 1px solid red !important;
      outline: red;
    }

    .buttons-area {
      display: flex;
      flex-direction: row;
      gap: 1rem;
    }
  }
`;

/**
 * @component MessageFormSection
 * @description Form section for creating/editing messages
 */
export const SendMessageFormSection = styled.section`
  ${({ isLoadingSendMessage }) => {
    if (isLoadingSendMessage) {
      return `
        display: flex;
        justify-content: center;
        align-items: center;
    `;
    }

    return `
      display: grid;
      gap: 1rem;
    `;
  }}

  min-width: 18rem;
  min-height: 252.594px;
  padding: 1rem 1.5rem 1rem;

  background-color: #fff;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  border-left: 1px solid rgba(207, 207, 207, 0.64);

  max-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;

  form {
    display: flex;
    gap: 1rem;
    flex-direction: column;
  }

  label {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  select,
  input {
    height: 2rem;
    color: rgb(67, 67, 67);
    border: 1px solid rgba(207, 207, 207, 0.64);
    border-radius: 0.3rem;
    background-color: #fff;
    transition:
      border-color 0.3s,
      color 0.3s;
  }

  select {
    width: 100%;
    padding: 0 1rem;
  }

  input {
    padding: 0 1.7rem;
  }

  select:focus,
  input[type='radio']:focus {
    border-color: #000;
    color: #000;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
    outline: none;
  }

  small {
    justify-self: end;
    color: #666;
    font-size: 0.55rem;
    padding-right: 0.5rem;
  }

  .select-small {
    max-width: 6rem;
    padding: 0 0.5rem;
  }

  .label-legend {
    display: grid;
  }

  .buttons-area {
    display: flex;
    justify-content: end;
    column-gap: 1rem;
    width: 100%;
  }

  .button-send {
    align-self: end;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .error {
    border: 1px solid red !important;
    outline: red;
  }
`;

export const SendMessageTitle = styled.h2`
  color: #444;
  padding-left: 1.2rem;
  font-size: 1.1rem;
  font-weight: 500;
`;

export const StyledAutocomplete = styled(Autocomplete)`
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || '100%'};
  /* padding: 0.8rem 0.3rem; */

  font-size: 0.9rem;
  border: none;
  border-radius: 0.3125rem;
  border-inline: 0.5px solid transparent;

  color: #ffffff;

  /* box-shadow: -2px 1px 7px -5px rgba(0, 0, 0, 0.6); */
  transition: all 0.1s ease-in-out;

  :hover {
    cursor: pointer;
  }

  :active {
    transform: scale(0.98);
    background-color: ${({ bgcoloractive }) => bgcoloractive || '#3275e0'};
    box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.2);
  }

  :focus {
    outline: none;
  }

  .MuiOutlinedInput-root {
    padding: 0;
    background: transparent;
    color: inherit;
    font-size: inherit;
    border-radius: inherit;
  }

  .MuiOutlinedInput-root.MuiInputBase-sizeSmall {
    padding: 0;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputBase-input {
    padding: 0;
  }

  &.error .MuiOutlinedInput-notchedOutline {
    border: 1px solid red !important;
    outline: red;
  }
`;

/**
 * @component MessageFormSection
 * @description Form section for creating/editing messages
 */
export const ConfirmSendMessageFormSection = styled.section`
  display: grid;
  gap: 0.8rem;

  max-width: 18rem;
  padding: 1rem 1.5rem;

  background-color: #fff;
  box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
  border-left: 1px solid rgba(207, 207, 207, 0.64);

  max-height: 100%;
  overflow-x: hidden;
  overflow-y: hidden;

  form {
    display: flex;
    gap: 1rem;
    flex-direction: column;
  }

  label {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  select,
  input {
    height: 2rem;
    color: rgb(67, 67, 67);
    border: 1px solid rgba(207, 207, 207, 0.64);
    border-radius: 0.3rem;
    background-color: #fff;
    transition:
      border-color 0.3s,
      color 0.3s;
  }

  select {
    width: 100%;
    padding: 0 1rem;
  }

  input {
    padding: 0 1.7rem;
  }

  select:focus,
  input:focus,
  textarea:focus {
    border-color: #000;
    color: #000;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
    outline: none;
  }

  small {
    justify-self: end;
    color: #666;
    font-size: 0.55rem;
    padding-right: 0.5rem;
  }

  p {
    color: #666;
  }

  .bold {
    font-weight: 500;
  }

  .select-small {
    max-width: 5rem;
    padding: 0 0.5rem;
  }

  .label-legend {
    display: grid;
  }

  .buttons-area {
    display: flex;
    justify-content: end;
    column-gap: 1rem;
    width: 100%;
  }

  .button-send {
    justify-self: end;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .error {
    border: 1px solid red !important;
    outline: red;
  }
`;

export const ConfirmSendMessageTitle = styled.h2`
  color: #444;
  font-size: 1.1rem;
  font-weight: 500;
`;

/**
 * @component CategoryDeleteSection
 * @description Section for category deletion confirmation
 */
export const CategoryDeleteSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  padding-top: 1rem;
  gap: 1rem;

  input {
    height: 2.5rem;
    width: 95%;
    border: 0.5px solid rgba(207, 207, 207, 0.69);
    border-radius: 2px;
    font-size: 0.8rem;
    color: rgb(68, 68, 68);
    padding-left: 15px;
    align-items: center;
    box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
    outline: none;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: end;
    gap: 1rem;
  }
`;

/**
 * @component MessageDeleteSection
 * @description Section for message deletion confirmation
 */
export const MessageDeleteSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  padding-top: 1rem;
  gap: 1rem;

  input {
    height: 2.5rem;
    width: 95%;
    border: 0.5px solid rgba(207, 207, 207, 0.69);
    border-radius: 2px;
    font-size: 0.8rem;
    color: rgb(68, 68, 68);
    padding-left: 15px;
    align-items: center;
    box-shadow: 1px 1px 2px -1px rgba(207, 207, 207, 0.64);
    outline: none;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: end;
    gap: 1rem;
  }
`;

/**
 * @component ModalHeader
 * @description Header for modal dialogs
 */
export const ModalHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 100%;
  border-bottom: 1px solid rgba(207, 207, 207, 0.64);
`;
